import React, { cloneElement, forwardRef, isValidElement, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import cx from './cx';

const CONTROL =
  'block w-full rounded-control border bg-field text-sm text-fg placeholder:text-fg-muted/80 ' +
  'transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Label + control + hint/error. The child control receives id, aria-describedby
 * and aria-invalid automatically.
 */
export function Field({ label, hint, error, required, htmlFor, className, labelClassName, children }) {
  const autoId = useId();
  const id = htmlFor || (isValidElement(children) && children.props.id) || `f${autoId}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
      id,
      'aria-describedby': describedBy,
      'aria-invalid': error ? true : children.props['aria-invalid'],
      invalid: children.type && children.type.$$kitControl ? Boolean(error) : undefined,
    })
    : children;

  return (
    <div className={cx('min-w-0', className)}>
      {label && (
        <label htmlFor={id} className={cx('mb-1.5 block text-xs font-medium text-fg-muted', labelClassName)}>
          {label}
          {required && <span className="ms-0.5 text-loss" aria-hidden="true">*</span>}
        </label>
      )}
      {control}
      {hint && !error && <p id={hintId} className="mt-1 text-xs text-fg-muted">{hint}</p>}
      {error && <p id={errorId} role="alert" className="mt-1 text-xs text-loss">{error}</p>}
    </div>
  );
}

export const Input = forwardRef(function Input({ className, invalid, size = 'md', ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cx(CONTROL, size === 'sm' ? 'h-9 px-2.5 sm:h-8' : 'h-9 px-3', invalid ? 'border-loss' : 'border-line', className)}
      {...rest}
    />
  );
});
Input.$$kitControl = true;

export const Textarea = forwardRef(function Textarea({ className, invalid, rows = 4, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cx(CONTROL, 'px-3 py-2 leading-relaxed', invalid ? 'border-loss' : 'border-line', className)}
      {...rest}
    />
  );
});
Textarea.$$kitControl = true;

export const Select = forwardRef(function Select({ className, invalid, size = 'md', children, ...rest }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cx(CONTROL, 'appearance-none pe-9', size === 'sm' ? 'h-9 ps-2.5 sm:h-8' : 'h-9 ps-3', invalid ? 'border-loss' : 'border-line', className)}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
    </div>
  );
});
Select.$$kitControl = true;

/** Accessible on/off switch (role="switch"). */
export function Switch({ checked, onChange, label, description, disabled, id, className, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy }) {
  const autoId = useId();
  const switchId = id || `sw${autoId}`;
  const descId = description ? `${switchId}-desc` : undefined;
  return (
    <div className={cx('flex items-start justify-between gap-4', className)}>
      {(label || description) && (
        <div className="min-w-0">
          {label && <label htmlFor={switchId} className="text-sm font-medium text-fg">{label}</label>}
          {description && <p id={descId} className="mt-0.5 text-xs text-fg-muted">{description}</p>}
        </div>
      )}
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={Boolean(checked)}
        aria-describedby={descId}
        aria-label={label ? undefined : ariaLabel}
        aria-labelledby={label ? undefined : ariaLabelledBy}
        disabled={disabled}
        onClick={() => onChange && onChange(!checked)}
        className={cx(
          // 24px visual track with a 40px invisible hit area for touch.
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 after:absolute after:-inset-2 after:content-['']",
          checked ? 'border-accent bg-accent' : 'border-fg-muted/40 bg-fg-muted/25'
        )}
      >
        <span
          aria-hidden="true"
          className={cx(
            'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1'
          )}
        />
      </button>
    </div>
  );
}
